/**
 * ============================================================================
 * AVATAR COMPONENT - 100% Ready
 * ============================================================================
 */

import React from 'react';

/**
 * Avatar Component
 * @param {Object} props
 * @param {string} props.src - Image source URL
 * @param {string} props.alt - Alt text
 * @param {string} props.name - Name for initials fallback
 * @param {string} props.size - Size (xs, sm, md, lg, xl)
 * @param {string} props.variant - Variant (circle, rounded, square)
 * @param {string} props.className - Additional classes
 * @param {string} props.status - Status indicator (online, offline, away)
 * @returns {JSX.Element}
 */
const Avatar = ({
  src,
  alt,
  name,
  size = 'md',
  variant = 'circle',
  className = '',
  status,
}) => {
  const sizeClasses = {
    xs: 'w-6 h-6 text-xs',
    sm: 'w-8 h-8 text-sm',
    md: 'w-10 h-10 text-base',
    lg: 'w-12 h-12 text-lg',
    xl: 'w-16 h-16 text-xl',
  };

  const variantClasses = {
    circle: 'rounded-full',
    rounded: 'rounded-lg',
    square: 'rounded-none',
  };

  const statusSizeClasses = {
    xs: 'w-1.5 h-1.5',
    sm: 'w-2 h-2',
    md: 'w-2.5 h-2.5',
    lg: 'w-3 h-3',
    xl: 'w-4 h-4',
  };

  const statusColors = {
    online: 'bg-green-500',
    offline: 'bg-gray-400',
    away: 'bg-yellow-500',
  };

  const getInitials = (name) => {
    if (!name) return '?';
    const parts = name.trim().split(' ');
    if (parts.length === 1) return parts[0][0].toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  };

  const getBgColor = (name) => {
    if (!name) return 'bg-gray-500';
    const colors = [
      'bg-blue-500',
      'bg-green-500',
      'bg-purple-500',
      'bg-pink-500',
      'bg-indigo-500',
      'bg-yellow-500',
      'bg-red-500',
      'bg-cyan-500',
    ];
    const index = name.charCodeAt(0) % colors.length;
    return colors[index];
  };

  const avatarSize = sizeClasses[size] || sizeClasses.md;
  const avatarVariant = variantClasses[variant] || variantClasses.circle;
  const statusSize = statusSizeClasses[size] || statusSizeClasses.md;

  return (
    <div className={`relative inline-block ${className}`}>
      {src ? (
        <img
          src={src}
          alt={alt || name || 'Avatar'}
          className={`
            ${avatarSize}
            ${avatarVariant}
            object-cover
            border-2 border-white dark:border-gray-800
            shadow-md
          `}
        />
      ) : (
        <div
          className={`
            ${avatarSize}
            ${avatarVariant}
            ${getBgColor(name)}
            text-white
            flex items-center justify-center
            font-semibold
            border-2 border-white dark:border-gray-800
            shadow-md
          `}
        >
          {getInitials(name)}
        </div>
      )}

      {/* Status Indicator */}
      {status && (
        <span
          className={`
            absolute bottom-0 right-0
            ${statusSize}
            ${statusColors[status] || statusColors.offline}
            ${variant === 'circle' ? 'rounded-full' : 'rounded'}
            border-2 border-white dark:border-gray-800
          `}
          aria-label={`Status: ${status}`}
        />
      )}
    </div>
  );
};

export default Avatar;
